import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useT } from "@/i18n";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "", company: "", privacy: false };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const t = useT().contact;

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const body = new FormData();
      ["name", "email", "phone", "subject", "message", "company"].forEach((k) => body.append(k, form[k]));
      const res = await fetch("/contact.php", { method: "POST", body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "send failed");
      }
      toast.success(t.success);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.message && err.message !== "send failed" ? err.message : t.error);
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border-b border-bone/25 bg-transparent py-4 text-base text-bone placeholder:text-bone/40 outline-none transition-colors duration-300 focus:border-terra";
  const label = "block text-xs uppercase tracking-[0.25em] text-bone/50";

  return (
    <section id="contact" className="bg-ink text-bone" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1440px] grid-cols-12 gap-x-8 gap-y-20 px-6 py-28 lg:px-12 lg:py-44">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-terra" data-testid="contact-kicker">{t.kicker}</p>
            <h2 className="font-serif text-4xl tracking-tight sm:text-5xl" data-testid="contact-heading">
              {t.heading}
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-bone/70" data-testid="contact-subtext">
              {t.subtext}
            </p>
          </Reveal>

          <div className="mt-14 space-y-2">
            <Reveal delay={0.1}>
              <a
                href="mailto:info@monferratoway.com"
                data-testid="contact-email-link"
                className="group flex items-center justify-between border-t border-bone/15 py-6 transition-colors duration-300 hover:border-terra"
              >
                <span className="flex items-center gap-5">
                  <Mail size={20} className="text-terra" />
                  <span className="text-xs uppercase tracking-[0.25em] text-bone/60">{t.emailLabel}</span>
                </span>
                <span className="flex items-center gap-3 font-serif text-xl tracking-tight">
                  info@monferratoway.com
                  <ArrowUpRight size={16} className="opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
                </span>
              </a>
            </Reveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="frame-dark relative" data-testid="contact-form">
              <div className="space-y-2">
                <label className={label} htmlFor="lead-name">{t.name}</label>
                <input
                  id="lead-name"
                  data-testid="contact-name-input"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.namePh}
                  className={field}
                />
              </div>
              <div className="mt-10 space-y-2">
                <label className={label} htmlFor="lead-email">{t.email}</label>
                <input
                  id="lead-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  maxLength={200}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="name@example.com"
                  className={field}
                />
              </div>
              <div className="mt-10 space-y-2">
                <label className={label} htmlFor="lead-phone">{t.phone}</label>
                <input
                  id="lead-phone"
                  type="tel"
                  data-testid="contact-phone-input"
                  maxLength={60}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t.phonePh}
                  className={field}
                />
              </div>
              <div className="mt-10 space-y-2">
                <label className={label} htmlFor="lead-subject">{t.subject}</label>
                <input
                  id="lead-subject"
                  data-testid="contact-subject-input"
                  maxLength={200}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder={t.subjectPh}
                  className={field}
                />
              </div>
              <div className="mt-10 space-y-2">
                <label className={label} htmlFor="lead-message">{t.message}</label>
                <textarea
                  id="lead-message"
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  maxLength={5000}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t.messagePh}
                  className={`${field} resize-none`}
                />
              </div>

              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                aria-hidden="true"
                data-testid="contact-honeypot"
              />

              <label className="mt-10 flex cursor-pointer items-start gap-4" data-testid="contact-privacy-check">
                <input
                  type="checkbox"
                  required
                  checked={form.privacy}
                  onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
                  className="mt-1 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-bone/40 transition-colors duration-300 checked:border-terra checked:bg-terra"
                  data-testid="contact-privacy-checkbox"
                />
                <span className="text-xs leading-relaxed text-bone/50">
                  {t.privacyA}{" "}
                  <a
                    href="https://www.iubenda.com/privacy-policy/65343853"
                    className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed underline transition-colors duration-300 hover:text-terra"
                    title="Privacy Policy"
                    data-testid="contact-privacy-link"
                  >
                    {t.privacyLink}
                  </a>{" "}
                  {t.privacyB}
                </span>
              </label>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group mt-10 flex w-full items-center justify-center gap-3 bg-terra px-8 py-5 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-bone hover:text-ink disabled:opacity-60"
              >
                {sending ? t.sending : t.submit}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
